/**
 * Build SEO `<title>` and `<meta name="description">` for a single
 * restaurant detail page. Used by Helmet on the detail page.
 *
 * Server-side mirror: utils/restaurantMetaBuilder.js — keep in sync.
 */

import { stateAbbrev } from "./stateAbbrev";

const DESCRIPTION_MAX = 155;

const CUISINE_OVERRIDES = {
  vegan: { fullyVegan: true },
  vegetarian: { omit: true },

  cafe: { render: "a cafe" },
  bakery: { render: "a bakery" },
  brewpub: { render: "a brewpub" },
  brewery: { render: "a brewery" },
  deli: { render: "a deli" },
  "food-truck": { render: "a food truck" },
  "food truck": { render: "a food truck" },
  "ice cream": { render: "an ice cream shop" },
  pizza: { render: "a pizzeria" },
  burgers: { render: "a burger spot" },
  tacos: { render: "a taco spot" },
  sandwich: { render: "a sandwich shop" },

  other: { omit: true },
  global: { omit: true },
  international: { omit: true },
  fusion: { omit: true },
  elevated: { omit: true },
  snacks: { omit: true },
  bowl: { omit: true },
  drinks: { omit: true },
  mocktails: { omit: true },
  condiments: { omit: true },
  tempeh: { omit: true },
  potato: { omit: true },
  pancakes: { omit: true },
  pie: { omit: true },
  jerky: { omit: true },
  chicken: { omit: true },
  healthy: { omit: true },
  "health food": { omit: true },
  breakfast: { omit: true },
  brunch: { omit: true },
  lunch: { omit: true },
  dinner: { omit: true },
};

function titleCase(str) {
  return str.replace(/\b\w/g, (l) => l.toUpperCase());
}

function articleFor(word) {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

function resolveCuisineTag(rawTag) {
  if (!rawTag || typeof rawTag !== "string") return null;
  const tag = rawTag.trim().toLowerCase();
  if (!tag) return null;
  if (tag.includes(",")) return null;

  const override = CUISINE_OVERRIDES[tag];
  if (override) {
    if (override.fullyVegan) return { fullyVegan: true };
    if (override.omit) return null;
    if (override.render) return { descriptor: override.render };
  }

  const label = titleCase(tag);
  return { descriptor: `${articleFor(label)} ${label} restaurant` };
}

function selectCuisineInfo(cuisines) {
  let fullyVegan = false;
  let descriptor = null;
  for (const raw of cuisines || []) {
    const resolved = resolveCuisineTag(raw);
    if (!resolved) continue;
    if (resolved.fullyVegan) {
      fullyVegan = true;
      continue;
    }
    if (!descriptor && resolved.descriptor) {
      descriptor = resolved.descriptor;
    }
  }
  if (fullyVegan && descriptor) {
    const stripped = descriptor.replace(/^(a|an)\s+/i, "");
    descriptor = `${articleFor("fully")} fully vegan ${stripped}`;
  } else if (fullyVegan && !descriptor) {
    descriptor = "a fully vegan restaurant";
  }
  return { fullyVegan, descriptor };
}

function buildLocationSuffix(restaurant) {
  const city = restaurant?.address?.address_locality;
  const region = restaurant?.address?.address_region;
  if (!city) return "";
  const abbr = region ? stateAbbrev(region) : "";
  return abbr ? `${city}, ${abbr}` : city;
}

function clamp(str, max) {
  if (str.length <= max) return str;
  const truncated = str.slice(0, max);
  const lastPeriod = truncated.lastIndexOf(". ");
  if (lastPeriod > max * 0.6) return truncated.slice(0, lastPeriod + 1);
  const lastSpace = truncated.lastIndexOf(" ");
  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : max - 1)}…`;
}

export function buildRestaurantMeta(restaurant) {
  const name = (restaurant?.name || "").trim();
  if (!name) {
    return {
      title: "Vegan Menu | VegifyIt",
      description:
        "Find vegan-friendly restaurants and plant-based menus on VegifyIt.",
    };
  }

  const isOnline = restaurant?.status === "ONLINE";
  const { fullyVegan, descriptor } = selectCuisineInfo(restaurant?.cuisine);
  const location = buildLocationSuffix(restaurant);
  const veganCount = (restaurant?.veganOptions || []).length;

  let titleCore;
  if (isOnline) {
    titleCore = `${name} — Vegan Pop-Up`;
  } else if (fullyVegan) {
    titleCore = `${name} — Vegan Restaurant`;
  } else {
    titleCore = `${name} — Vegan Menu`;
  }
  const title = location
    ? `${titleCore} in ${location} | VegifyIt`
    : `${titleCore} | VegifyIt`;

  const inLocation = location ? ` in ${location}` : "";
  const atDescriptor = descriptor ? `, ${descriptor}` : "";

  let opening;
  if (isOnline) {
    opening =
      veganCount > 0
        ? `Find ${veganCount} vegan dishes from ${name}, a plant-based pop-up vendor.`
        : `Find vegan dishes from ${name}, a plant-based pop-up vendor.`;
  } else if (fullyVegan) {
    opening = veganCount
      ? `Explore ${veganCount} dishes from the all-vegan menu at ${name}${atDescriptor}${inLocation}.`
      : `Explore the all-vegan menu at ${name}${atDescriptor}${inLocation}.`;
  } else {
    opening = veganCount
      ? `Explore ${veganCount} vegan dishes at ${name}${atDescriptor}${inLocation}.`
      : `Explore vegan dishes at ${name}${atDescriptor}${inLocation}.`;
  }

  const longTail = " See the menu, directions, and website on VegifyIt.";
  const shortTail = " See the menu on VegifyIt.";
  let description = opening + longTail;
  if (description.length > DESCRIPTION_MAX) {
    description = opening + shortTail;
  }
  if (description.length > DESCRIPTION_MAX) {
    description = clamp(description, DESCRIPTION_MAX);
  }

  return { title, description };
}
